import React, { useCallback } from 'react';
import DOMPurify from 'dompurify';
import { useNavigate } from 'react-router-dom';
import { markdownSubsetToHtml } from '../../utils/aiChatMarkdown';
import { logInfo } from '../../config_and_helpers/logger';
import './AiChatStyle.css';

const LOG_CONTEXT = 'AiChatMessageBody';

const PURIFY_OPTIONS = {
  ALLOWED_TAGS: [
    'p',
    'br',
    'strong',
    'em',
    'ul',
    'ol',
    'li',
    'code',
    'a',
  ],
  ALLOWED_ATTR: ['href', 'target', 'rel', 'class'],
  ALLOW_DATA_ATTR: false,
};

/**
 * Render assistant (markdown subset) or user (plain) message body.
 * In-app relative links ([Orders](/orders)) navigate in the SPA without a full reload.
 *
 * @param {Object} props
 * @param {string} props.content
 * @param {'user'|'assistant'} props.role
 * @returns {JSX.Element}
 */
const AiChatMessageBody = ({ content, role }) => {
  const navigate = useNavigate();

  const onClick = useCallback(
    event => {
      const anchor = event.target?.closest?.('a.ai-chat-app-link');
      if (!anchor) return;
      const href = anchor.getAttribute('href');
      if (!href || !href.startsWith('/') || href.startsWith('//')) return;
      event.preventDefault();
      logInfo(LOG_CONTEXT, 'in-app link navigate', { href });
      navigate(href);
    },
    [navigate]
  );

  if (role !== 'assistant') {
    return <div className="ai-chat-message-body">{content}</div>;
  }

  const dirty = markdownSubsetToHtml(content);
  const clean = DOMPurify.sanitize(dirty, PURIFY_OPTIONS);

  if (process.env.NODE_ENV === 'development' && dirty && !clean) {
    logInfo(LOG_CONTEXT, 'sanitizer emptied assistant html', {
      contentLength: content?.length,
    });
  }

  return (
    <div
      className="ai-chat-message-body ai-chat-message-body--md"
      onClick={onClick}
      dangerouslySetInnerHTML={{ __html: clean }}
    />
  );
};

export default AiChatMessageBody;